import {Middleware, AnyAction, createAction} from '@reduxjs/toolkit'

import {changeLocation, LocationChangeAction} from '@web/actions/application'
import {navActions} from '@web/actions/navigateTo'
import {OrderSheetPageSetType} from '@web/core/constants'
import {isMobile} from '@web/helpers/userAgent'
import {formSubmitToAnotherPage} from '@web/middlewares/browserEventEmitter'
import {orderInterrupted} from '@web/middlewares/orderInterrupted'

/** 주문 실패 사유 수집 (화면 block) */
type Failure = {
    type: 'failure'
    info: {
        code: string
        message: string
    }
}

/** 주문 정보 수집 */
type Info = {
    type: 'info'
    info: {
        message: string
    }
}

/** 결제 valid 실패 사유 수집 (FE) */
type OrderTry = {
    type: 'orderTry'
    info: {
        code: string
        from?: string
    }
}
/** 가주문 실패 사유 수집 (BE) */
type OrderFail = {
    type: 'orderFail'
    info: {
        orderNo?: string
        orderProcessId?: string
        code: string
        message: string
        isUnknown: boolean
    }
}
/** 진주문 결제 승인 실패 사유 수집 */
type OrderApplyFail = {
    type: 'orderApplyFail'
    info: {
        pageSet: Maybe<OrderSheetPageSetType>
        orderNo?: Maybe<string>
        orderSheetId?: Maybe<string>
        orderProcessId?: string
        status?: string
        code?: string
        message?: string
    }
}

type ChangeHopeDate = {
    type: 'changeHopeDate'
    info: {
        pageSet: Maybe<OrderSheetPageSetType>
        code?: string
        popupUrl?: string
        isSuccess?: boolean
        selectedDate?: string
        errorCode?: string
        errorMessage?: string
        errorData?: object
    }
}

type ActionPayloadType = Failure | Info | OrderTry | OrderFail | OrderApplyFail | ChangeHopeDate

export const loggingAction = createAction<ActionPayloadType>('logger/info2')

export const actionLoggerCreator =
    <AppState>({
        callbackLeaveAction,
        callbackLogAction,
    }: {
        callbackLeaveAction?: (argument: {
            state: AppState
            action: AnyAction
            path?: string
            type: 'push' | 'replace' | 'back' | 'backClose' | 'close' | 'popup' | 'form'
        }) => void
        callbackLogAction?: (argument: {state: AppState; action: AnyAction; log: ActionPayloadType}) => void
    }): Middleware<AppState> =>
    ({getState}) =>
    (next) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (action: any) => {
        const state = getState()
        const common = {
            state,
            action,
        }
        const matchesWithNavigatorAction = Object.values(navActions).some((navAction) =>
            navAction.pending.match(action),
        )
        if (orderInterrupted.match(action)) {
            action.payload.forEach((payload) => {
                const type = payload.type
                if (type === 'REDIRECT') {
                    if (payload.push && payload.url) {
                        callbackLeaveAction?.({
                            ...common,
                            type: 'push',
                            path: payload.url,
                        })
                    } else {
                        callbackLeaveAction?.({
                            ...common,
                            type: 'replace',
                            path: payload.url ?? '',
                        })
                    }
                } else if (type === 'CLOSE') {
                    callbackLeaveAction?.({state, action, type: 'close'})
                } else if (type === 'BACK') {
                    callbackLeaveAction?.({state, action, type: payload.shouldClose ? 'backClose' : 'back'})
                }
            })
        } else if (changeLocation.match(action)) {
            const type = action.payload.type
            if (type === LocationChangeAction.PUSH) {
                callbackLeaveAction?.({state, action, path: action.payload.url, type: 'push'})
            } else if (type === LocationChangeAction.REPLACE) {
                callbackLeaveAction?.({state, action, path: action.payload.url, type: 'replace'})
            } else if (type === LocationChangeAction.BACK) {
                callbackLeaveAction?.({state, action, type: 'back'})
            } else if (type === LocationChangeAction.CLOSE) {
                callbackLeaveAction?.({state, action, type: 'close'})
            }
        } else if (matchesWithNavigatorAction) {
            const locationChangeUsed = isMobile || action.meta.arg?.forceLocationChange
            const historyStackType = (action.meta.arg?.historyType ?? 'push').toLowerCase()
            const navigateType = locationChangeUsed ? historyStackType : 'popup'

            callbackLeaveAction?.({state, action, type: navigateType})
        } else if (formSubmitToAnotherPage.match(action)) {
            const path = action.payload?.fields?.['rurl'] || action.payload.url || ''
            callbackLeaveAction?.({state, action, path, type: 'form'})
        } else if (callbackLogAction && loggingAction.match(action)) {
            callbackLogAction({
                state: getState(),
                action,
                log: action.payload,
            })
        }
        return next(action)
    }
